import { useState } from "react";
import { Button } from "../../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Trash2, Download, Eye, Calendar as CalendarIcon, Clock, File, FileText, Image, Activity, Pencil, Loader2 } from "lucide-react";
import { canPreview } from "../../lib/filePreview";

function getFileIcon(contentType) {
  if (contentType?.startsWith("image/")) return Image;
  if (contentType === "application/pdf") return FileText;
  return File;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Timeline({ updates, handleDeleteUpdate, handleEditUpdate, handlePreview, handleDownload, user, t, formatDate }) {
  // Which update is open for editing, and the text as it currently stands.
  // One pair for the whole list: only one update can be edited at a time, and
  // holding this per card would keep a draft alive behind every other one.
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const startEditing = (update) => {
    setEditingId(update.id);
    setDraft(update.content || "");
  };

  const cancelEditing = () => {
    // The draft is dropped rather than kept: somebody pressing Cancel has
    // changed their mind about the change, not about editing later.
    setEditingId(null);
    setDraft("");
  };

  const saveEditing = async (update) => {
    const text = draft.trim();
    if (!text || text === update.content) return;
    setSaving(true);
    const ok = await handleEditUpdate(update.id, text);
    setSaving(false);
    // Left open on failure, with the draft intact: the request failing is no
    // reason to throw away what was typed.
    if (ok) cancelEditing();
  };

  if (updates.length === 0) {
    return <div className="text-center py-12 text-slate-400">{t("project.noUpdates")}</div>;
  }

  return (
    <div className="relative">
      {updates.map((update, index) => {
        const isSystem = update.update_type === "system";

        // System entries record what happened to a project rather than what
        // somebody said about it. They belong in the story, but as a quiet
        // marker: giving them a full card would make a status change look like
        // news, and would drown the updates people actually wrote.
        if (isSystem) {
          return (
            <div key={update.id} data-testid={`update-item-${update.id}`} className="relative pl-8 pb-6">
              {index < updates.length - 1 && <div className="timeline-line" />}
              <div className="timeline-dot" style={{ backgroundColor: "#cbd5e1" }} />
              {/* Wraps: the sentence, the author and the date on one line are
                  wider than a phone. */}
              <div className="flex items-start gap-x-2 gap-y-0.5 text-xs text-slate-400 py-1 flex-wrap">
                <Activity className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="text-slate-500 break-words">{update.content}</span>
                <span className="text-slate-300">&middot;</span>
                <span>{update.author_name}</span>
                <span className="text-slate-300">&middot;</span>
                <span>{formatDate(update.created_at)}</span>
              </div>
            </div>
          );
        }

        // Only the author, and only on something a person wrote. A meeting
        // entry is written by the application when one is scheduled, so it has
        // no author entitled to reword it - the server refuses those too.
        const isMeeting = update.update_type === "meeting";
        const canEdit = !isMeeting && user?._id && String(update.author_id) === String(user._id);
        const isEditing = editingId === update.id;

        return (
          <div key={update.id} data-testid={`update-item-${update.id}`} className="relative pl-8 pb-8">
            {index < updates.length - 1 && <div className="timeline-line" />}
            <div className="timeline-dot" style={{ backgroundColor: update.author_color || '#3730A3' }} />
            <div className={`${isMeeting ? 'update-card-meeting' : 'update-card'} rounded-lg p-4 shadow-sm`}>
              {/* The author, the date and the meeting badge wrap onto a second
                  line rather than pushing the buttons off the card. */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-x-3 gap-y-1 flex-wrap min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0" style={{ backgroundColor: update.author_color || '#3730A3' }}>
                      {update.author_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <span className="text-sm font-semibold truncate" style={{ color: update.author_color || '#3730A3' }}>{update.author_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 shrink-0">
                    <CalendarIcon className="w-3 h-3" />
                    <span>{formatDate(update.created_at)}</span>
                    {/* One word, and only once there is something to say. Not a
                        history and not a name: the author is the only person
                        who can have done it. */}
                    {update.edited_at && (
                      <span className="text-slate-400" data-testid={`update-edited-${update.id}`}>
                        &middot; {t("project.edited")}
                      </span>
                    )}
                  </div>
                  {isMeeting && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-sky-100 text-sky-700 shrink-0">
                      <Clock className="w-3 h-3" />
                      {t("project.meeting")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {canEdit && !isEditing && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEditing(update)}
                      data-testid={`edit-update-${update.id}`}
                      aria-label={t("project.editUpdate")}
                      className="h-8 w-8 shrink-0 text-slate-400 hover:text-indigo-700 hover:bg-indigo-50"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  {user?.role === "admin" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-update-${update.id}`}
                          aria-label={t("project.deleteUpdate")}
                          className="h-8 w-8 shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("project.deleteUpdate")}</AlertDialogTitle>
                          <AlertDialogDescription>{t("project.deleteUpdateDesc")}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-slate-200">{t("common.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteUpdate(update.id)} data-testid={`confirm-delete-update-${update.id}`} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>

              {isEditing ? (
                /* In place, so the update does not move to be corrected. */
                <div className="mb-3">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Escape") cancelEditing(); }}
                    data-testid={`edit-update-input-${update.id}`}
                    autoFocus
                    rows={4}
                    className="w-full rounded-lg border border-slate-200 p-3 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-y"
                  />
                  <div className="flex flex-wrap justify-end gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelEditing}
                      data-testid={`cancel-edit-update-${update.id}`}
                      className="border-slate-200"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => saveEditing(update)}
                      disabled={saving || !draft.trim() || draft.trim() === update.content}
                      data-testid={`save-edit-update-${update.id}`}
                      className="bg-indigo-700 hover:bg-indigo-600 text-white"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.save")}
                    </Button>
                  </div>
                </div>
              ) : (
                /* break-words as well as pre-wrap: pre-wrap keeps the line
                   breaks somebody typed but does nothing for a pasted URL, which
                   is one unbroken word and stretched the card off the screen. */
                <p className="text-slate-700 whitespace-pre-wrap break-words mb-3">{update.content}</p>
              )}

              {update.attachment && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  {(() => { const FileIcon = getFileIcon(update.attachment.content_type); return <FileIcon className="w-5 h-5 text-indigo-500 shrink-0" />; })()}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{update.attachment.original_filename}</p>
                    <p className="text-xs text-slate-400">{formatFileSize(update.attachment.size)}</p>
                  </div>
                  {/* file.view and file.download rather than project.*: these two
                      buttons are icons alone, so the label is all a screen
                      reader has, and the project.* keys do not exist - it would
                      have read out the key itself. */}
                  <div className="flex items-center gap-1 shrink-0">
                    {canPreview(update.attachment.content_type) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handlePreview(update.attachment)}
                        aria-label={t("file.view")}
                        className="h-9 w-9 text-slate-500 hover:text-indigo-700"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDownload(update.attachment)}
                      aria-label={t("file.download")}
                      className="h-9 w-9 text-slate-500 hover:text-indigo-700"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
